<template>
  <div :class="bem()">
    <template v-if="!verificationError && !verificationRes">
      <div :class="bem('spinner-container')">
        <VisionLoadingSpinner :loading="true" />
      </div>
      <p :class="bem('text')">Verifying your email...</p>
    </template>
    <template v-if="verificationError">
      <div :class="bem('icon-container')">
        <VisionIcon
          icon="error"
          colour="black"
        />
      </div>
      <p
        :class="bem('text')"
      >
        {{ verificationError }}
        <span :class="bem('sub-text')">Please try again later.</span>
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
const bem = useBEM('StravaVerifying')
const route = useRoute()
const router = useRouter()
const { $sentry, $track } = useNuxtApp()
const { public: { competitions: { apiEndpoint } } } = useRuntimeConfig()

type StravaVerifyingProps = {
  slug: string
  competitionId: number
}

const props = defineProps<StravaVerifyingProps>()

const verificationRes = ref(null)
const verificationError = ref('')

onMounted(async () => {
  try {
    const verificationRes = await $fetch(`${apiEndpoint}/verify/`, {
      method: 'POST',
      body: {
        v1: route.query.v1,
        v2: route.query.v2,
        comp_id: props.competitionId,
        slug: props.slug,
        host: `${window.location.origin}/${route.params.locale}/${props.slug}`
      }
    })

    if(verificationRes.portal_link) {
      const responseParams = new URL(verificationRes.portal_link).searchParams

      $track.competition.viewed({ slug: props.slug })

      router.replace({
        query: {
          mode: responseParams.get('mode'),
          key: responseParams.get('key')
        }
      })
    } else {
      verificationError.value = 'Unable to complete verification'
      throw createError({ message: verificationError.value })
    }
  } catch(verificationError) {
    $sentry.captureException(verificationError)
  }
})
</script>

<style lang="postcss" scoped>
.StravaVerifying {
  @apply py-4;
}

.StravaVerifying__spinner-container {
  @apply relative h-12 mb-2;
}

.StravaVerifying__icon-container {
  @apply flex justify-center mb-4;
}

.StravaVerifying__text {
  @apply smMax:text-xxs text-sm text-center text-off-black font-bold;
}

.StravaVerifying__sub-text {
  @apply smMax:text-xxs text-sm block font-normal;
}
</style>